/**
 * Which window Escape belongs to, and which plane each one sits on —
 * docs/24 §7.5, §7.6, §7.10.
 */

export type Plane = "float" | "dialog" | "docked";

/**
 * Floats take 40–49 and docked windows 60–69, with the console's own dialogs at
 * 50 between them: a docked drill is by definition the thing that stopped the
 * queue, and opening one BEHIND a float is a read nobody can see.
 */
export const PLANE_BANDS: Readonly<Record<Plane, readonly [number, number]>> = {
  float: [40, 49],
  dialog: [50, 50],
  docked: [60, 69],
};

export interface Claim {
  readonly id: number;
  readonly plane: Plane;
  readonly zIndex: number;
  /**
   * §7.6: a docked window ALWAYS claims; a float claims only while it holds
   * focus; nothing claims while a cell editor, an open `<select>` or an AG Grid
   * popup is up.
   */
  readonly claimsEscape: () => boolean;
  readonly onEscape: () => void;
}

export interface LayerStack {
  claim(claim: Omit<Claim, "id" | "zIndex">): { zIndex: number; release: () => void };
  /** The claim Escape belongs to right now, or `null`. */
  front(): Claim | null;
  /** True when a window took the key. The caller then does not let it bubble. */
  escape(): boolean;
  /** How many floats are on the glass — the cascade counts them (§7.5). */
  count(plane: Plane): number;
  all(): readonly Claim[];
}

/**
 * §7.10: a window CLAIMS a plane on mount and drops it on unmount, so a window
 * opened over another sits above it because it ARRIVED LATER. Two windows
 * sharing a plane stack by DOM order.
 *
 * Why raising dialogs above 69 is not the fix for a drill that contains a
 * dialog: the numbers are a fixed ladder, so it answers this case and leaves the
 * next one. A register behind a picker's ⋯, with its own «new category», would
 * open its form at 50 beneath a docked register at 60 — under that register's
 * own scrim, reading as a button that does nothing. Claiming solves it once.
 */
export function createLayerStack(): LayerStack {
  const claims: Claim[] = [];
  let nextId = 1;

  return {
    claim(spec) {
      const [floor, ceiling] = PLANE_BANDS[spec.plane];
      const onPlane = claims.filter((existing) => existing.plane === spec.plane).length;
      const claim: Claim = {
        ...spec,
        id: nextId++,
        zIndex: Math.min(floor + onPlane, ceiling),
      };
      claims.push(claim);
      return {
        zIndex: claim.zIndex,
        release: () => {
          const at = claims.indexOf(claim);
          if (at >= 0) claims.splice(at, 1);
        },
      };
    },

    front() {
      // Last claimant first: the window that arrived most recently is in front.
      for (let index = claims.length - 1; index >= 0; index -= 1) {
        const claim = claims[index]!;
        if (claim.claimsEscape()) return claim;
      }
      return null;
    },

    escape() {
      // ONE listener for every window on the glass, handing the key to the front
      // claimant.
      // Failure mode of a listener per window: `stopPropagation` stops an event
      // reaching other TARGETS, not other listeners on the same one, so four
      // windows all answer one Escape and a reader stepping back one loses the
      // whole stack.
      // Failure mode of a capture-phase listener, measured in Premier: it took
      // Escape from a right-click menu over the register behind an undocked
      // panel — the menu stood there while the panel closed underneath it. So
      // this runs on the bubble phase and `claimsEscape` is what abstains.
      const claim = this.front();
      if (!claim) return false;
      claim.onEscape();
      return true;
    },

    count(plane) {
      return claims.filter((claim) => claim.plane === plane).length;
    },

    all() {
      return claims;
    },
  };
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One step down and across per float already on the glass, wrapping after five. */
export const CASCADE_STEP = 28;
export const CASCADE_WRAP = 5;

/**
 * Failure mode this exists for: `z-index` orders two boxes; it does nothing
 * about two boxes at the SAME PLACE and the SAME SIZE. A second one opening
 * dead centre over the first reads as one window changing its content.
 */
export function cascadeOffset(floatsAlready: number): { dx: number; dy: number } {
  const step = floatsAlready % CASCADE_WRAP;
  return { dx: step * CASCADE_STEP, dy: step * CASCADE_STEP };
}

/**
 * The head is the only handle, so a box dragged past the top edge can never be
 * brought back. Clamp on every pointer move AND on every window resize, keeping
 * `HEAD_ON_GLASS` pixels of the head reachable.
 */
export const HEAD_ON_GLASS = 96;

export function clampRect(rect: Rect, viewport: { width: number; height: number }): Rect {
  const maxX = viewport.width - HEAD_ON_GLASS;
  const maxY = viewport.height - HEAD_ON_GLASS;
  // The left edge may go negative — a wide window whose right half is on screen
  // is usable — but never so far that the head's grab area leaves the glass.
  const minX = HEAD_ON_GLASS - rect.width;
  return {
    ...rect,
    x: Math.min(Math.max(rect.x, minX), maxX),
    y: Math.min(Math.max(rect.y, 0), maxY),
  };
}
